import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { ChartEmptyState, CHART_EMPTY_STATE_NO_DATA_LABEL } from '@/ui/composites';
import { StatusPieCardSkeleton } from '@/ui/composites/status/StatusPieCardSkeleton';
import { Box, LineChartSkeleton, Skeleton } from '@/ui/primitives';
import type { ReactElement } from 'react';
export {
  resolveStackedStatusCardIcon,
  resolveStackedStatusCardIconColor,
} from './StackedMetricsStatusChartSection.icons';

const LONG_SKELETON_HEIGHT = componentSizes.dataGrid.skeletonHeight;
const LONG_SKELETON_RADIUS = '999px';
const shortMonthDayFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
});

const formatShortMonthDay = (label: string): string => {
  const trimmed = label.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return shortMonthDayFormatter.format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const slashMatchWithYear = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slashMatchWithYear) {
    const [, day, month, year] = slashMatchWithYear;
    return shortMonthDayFormatter.format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const slashMatch = /^(\d{1,2})\/(\d{1,2})$/.exec(trimmed);
  if (slashMatch) {
    const [, day, month] = slashMatch;
    const currentYear = new Date().getFullYear();
    return shortMonthDayFormatter.format(new Date(currentYear, Number(month) - 1, Number(day)));
  }

  return trimmed;
};

export const renderMiniMetricEmptyState = (_title: string, height: number): ReactElement => (
  <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <ChartEmptyState
      title={CHART_EMPTY_STATE_NO_DATA_LABEL}
      contentSx={{ transform: 'translateY(-6px)' }}
      variant="metricCard"
    />
  </Box>
);

export const renderMiniMetricLoadingState = (height: number): ReactElement => (
  <LineChartSkeleton
    height={height}
    leftInset={`calc(${s('md')} + ${s('none')})`}
    bottomInset={s('md')}
    showPlotAreaBackground={false}
    hideYAxisLine
  />
);

export const renderStatusPieLoadingState = (height: number): ReactElement => {
  const legendRows = [
    { labelWidth: '70%', valueWidth: 44 },
    { labelWidth: '52%', valueWidth: 36 },
    { labelWidth: '62%', valueWidth: 28 },
    { labelWidth: '44%', valueWidth: 38 },
  ] as const;
  const pieSize = Math.min(172, Math.max(156, Math.round(height * 0.52)));
  const pieInnerSize = Math.round(pieSize * 0.52);
  const legendMaxWidth = Math.min(260, Math.max(180, Math.round(pieSize * 1.38)));
  return (
    <Box
      sx={{
        width: '100%',
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: { xs: s('2xl'), lg: s('3xl') },
        pt: { xs: s('xl'), lg: s('3xl') },
      }}
    >
      <StatusPieCardSkeleton size={pieSize} innerSize={pieInnerSize} />
      <Box
        sx={{
          width: '100%',
          maxWidth: legendMaxWidth,
          display: 'grid',
          rowGap: { xs: s('md'), lg: s('lg') },
          px: { xs: s('lg'), lg: s('xl') },
          pt: s('none'),
          justifyItems: 'stretch',
          mx: 'auto',
        }}
      >
        {legendRows.map((row, index) => (
          <Box
            key={`pie-legend-skeleton-${index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              alignItems: 'center',
              columnGap: s('lg'),
              width: '100%',
            }}
          >
            <Skeleton
              variant="rounded"
              width={row.labelWidth}
              height={LONG_SKELETON_HEIGHT}
              sx={{
                display: 'block',
                flexShrink: 0,
                minHeight: LONG_SKELETON_HEIGHT,
                maxHeight: LONG_SKELETON_HEIGHT,
                borderRadius: LONG_SKELETON_RADIUS,
              }}
            />
            <Skeleton
              variant="rounded"
              width={row.valueWidth}
              height={LONG_SKELETON_HEIGHT}
              sx={{
                display: 'block',
                flexShrink: 0,
                minHeight: LONG_SKELETON_HEIGHT,
                maxHeight: LONG_SKELETON_HEIGHT,
                borderRadius: LONG_SKELETON_RADIUS,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const renderStatusChartLegendLoadingState = (): ReactElement => {
  const itemWidths = [88, 82, 86, 90] as const;
  return (
    <Box
      sx={{
        width: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        alignItems: 'center',
        columnGap: s('lg'),
        rowGap: s('xs'),
        minWidth: 0,
        boxSizing: 'border-box',
        transform: `translateY(${s('xs')})`,
      }}
    >
      {itemWidths.map((width, index) => (
        <Skeleton
          key={`status-legend-skeleton-${index}`}
          variant="rounded"
          width={width}
          height={LONG_SKELETON_HEIGHT}
          sx={{
            display: 'block',
            flexShrink: 0,
            minHeight: LONG_SKELETON_HEIGHT,
            maxHeight: LONG_SKELETON_HEIGHT,
            borderRadius: LONG_SKELETON_RADIUS,
          }}
        />
      ))}
    </Box>
  );
};

export const renderMiniMetricHeaderLoadingState = (): ReactElement => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: s('md'),
    }}
  >
    <Skeleton
      variant="rounded"
      width={92}
      height={LONG_SKELETON_HEIGHT}
      sx={{ borderRadius: LONG_SKELETON_RADIUS }}
    />
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Skeleton
        variant="rounded"
        width={78}
        height={LONG_SKELETON_HEIGHT}
        sx={{ borderRadius: LONG_SKELETON_RADIUS }}
      />
    </Box>
  </Box>
);

export const formatStackedStatusChartLabel = (label: string): string => {
  return formatShortMonthDay(label);
};
